import type {
	CommentCreateDto,
	CommentListResponseDto,
	CommentResponseDto,
} from "Infrastructure/Api/Api";
import { useUser } from "Settings/User/Api/Query/useUserProfileQuery";
import { useUserList } from "Settings/User/Api/Query/useUsersListQuery";
import { useAvatarFilePath } from "Settings/User/Hooks/useAvatarFilePath";
import { BlankCard } from "Shared/Components/Cards/BlankCard";
import { isDeleteCommentConfirmationModalOpenAtom } from "Shared/Components/Comments/Atom/isDeleteConfirmationModalOpen";
import { CommentDeleteConfirmationModal } from "Shared/Components/Comments/Components/CommentDeleteConfirmationModal";
import { CommentForm } from "Shared/Components/Comments/Form/CommentForm";
import { Delete, Edit } from "@mui/icons-material";
import { Avatar, Box, Stack, Tooltip, Typography } from "@mui/material";
import type { UseMutationResult } from "@tanstack/react-query";
import dayjs from "dayjs";
import DOMPurify from "dompurify";
import { useAtom } from "jotai";
import { useMemo, useState } from "react";

type CommentProps = {
	comment: CommentListResponseDto;
	updateCommentMutation: UseMutationResult<
		CommentResponseDto,
		Error,
		{ data: CommentCreateDto; commentId: number },
		unknown
	>;
	deleteCommentMutation: UseMutationResult<void, Error, number, unknown>;
};

const avatarStyle = {
	borderRadius: "50%",
	width: "40px",
	height: "40px",
	border: "2px solid #fff",
};

/**
 * CommentDetail component displays the details of a comment, including the author's avatar, name, and the comment's content.
 * It also provides functionality for editing and deleting the comment.
 */
export function CommentDetail({
	comment,
	updateCommentMutation,
	deleteCommentMutation,
}: CommentProps) {
	const [isEditable, setIsEditable] = useState(false);
	const [currentCommentModalOpen, setCurrentCommentModalOpen] = useAtom(
		isDeleteCommentConfirmationModalOpenAtom,
	);
	const { user } = useUser();
	const { data: users } = useUserList();
	const { mutate: updateComment, isPending } = updateCommentMutation;

	const handleUpdateSubmit = (data: { message: string }) => {
		updateComment({ data, commentId: comment.id });
		setIsEditable(false);
	};
	const handleEditClick = () => {
		setIsEditable((prev) => !prev);
	};

	const handleOpenDeleteCommentModal = () => {
		setCurrentCommentModalOpen(comment.id);
	};

	const oneHourAgo = useMemo(() => dayjs().subtract(1, "hour"), []);

	const isCreatedLessThanHourAgo = useMemo(
		() => dayjs(comment.dateCreated).isAfter(oneHourAgo),
		[comment.dateCreated, oneHourAgo],
	);

	const showEditIcon = useMemo(() => {
		if (!updateCommentMutation || isEditable || user?.id !== comment.userId) {
			return false;
		}
		return isCreatedLessThanHourAgo;
	}, [
		updateCommentMutation,
		isEditable,
		user?.id,
		comment.userId,
		isCreatedLessThanHourAgo,
	]);

	const operator = users?.find((user) => user.id === comment.userId);
	const avatarFilePath = useAvatarFilePath(
		comment.userId,
		operator?.avatarFileName,
	);

	return (
		<BlankCard>
			<Stack direction="row" spacing={2} p={1}>
				<Tooltip title={`${comment.userName} ${comment.userSurname}`}>
					<Avatar src={avatarFilePath} alt="Profile image" sx={avatarStyle} />
				</Tooltip>

				<Stack alignItems="flex-start">
					<Stack direction="row" spacing={1} alignItems="center">
						{!comment.dateUpdated && (
							<Typography variant="caption">
								{dayjs(comment.dateCreated).format("DD.MM.YYYY HH:mm:ss")}
							</Typography>
						)}
						{comment.dateUpdated && (
							<Typography variant="caption">
								Edited{" "}
								{dayjs(comment.dateUpdated).format("DD.MM.YYYY HH:mm:ss")}
							</Typography>
						)}
						{showEditIcon && (
							<Box
								width={20}
								height={20}
								display="flex"
								alignItems="center"
								sx={{ cursor: "pointer" }}
								onClick={handleEditClick}
							>
								<Edit fontSize="small" />
							</Box>
						)}
						{isEditable && user?.id === comment.userId && (
							<Box
								width={20}
								height={20}
								display="flex"
								alignItems="center"
								sx={{
									cursor: "pointer",
									color: (theme) => theme.palette.warning.main,
								}}
								onClick={handleOpenDeleteCommentModal}
							>
								<Delete fontSize="small" />
							</Box>
						)}
					</Stack>
					{!updateCommentMutation ||
						(!isEditable && (
							<Box>
								<div
									// biome-ignore lint/security/noDangerouslySetInnerHtml: "none"
									dangerouslySetInnerHTML={{
										__html: DOMPurify.sanitize(comment.message),
									}}
								/>
							</Box>
						))}
				</Stack>
			</Stack>
			{updateCommentMutation && isEditable ? (
				<CommentForm
					onSubmit={handleUpdateSubmit}
					isPending={isPending}
					message={comment.message}
					handleCancel={() => setIsEditable(false)}
					isUpdate
				/>
			) : (
				<></>
			)}
			{currentCommentModalOpen === comment.id ? (
				<CommentDeleteConfirmationModal
					commentId={comment.id}
					deleteCommentMutation={deleteCommentMutation}
					closeEdit={() => setIsEditable(false)}
				/>
			) : (
				<></>
			)}
		</BlankCard>
	);
}
